// Sound notifications — separate from (and independent of) the browser push
// toggle in lib/push.js. Push permission is inherently per-browser (it's the
// browser's own Notification.permission), so this follows the same model:
// a plain localStorage preference rather than something synced through the
// user's account, on or off per device just like push is.
const ON_KEY = "sound_notify_on";
const CHOICE_KEY = "sound_notify_choice";

// Short synthesized tones (Web Audio oscillators) instead of shipped audio
// files — no assets to fetch, works offline, and each one is defined in a
// couple of numbers. `notes` is one or more frequencies played in sequence;
// `drop`/`shimmer` are small per-sound flourishes (a pitch drop for "Pop", a
// quiet high harmonic for "Bell").
export const SOUNDS = [
  { name: "Chime", tag: "default", notes: [880, 1175], type: "sine", decay: 0.55 },
  { name: "Ping", tag: "crisp", notes: [1320], type: "sine", decay: 0.28 },
  { name: "Pop", tag: "soft", notes: [180, 90], type: "sine", decay: 0.16, drop: true },
  { name: "Marimba", tag: "warm", notes: [523.25], type: "triangle", decay: 0.5 },
  { name: "Bell", tag: "bright", notes: [659.25], type: "sine", decay: 0.9, shimmer: true },
  { name: "Note", tag: "quiet", notes: [440], type: "triangle", decay: 0.3 },
];
const DEFAULT_SOUND = "Chime";

export function isSoundOn() {
  try {
    return window.localStorage.getItem(ON_KEY) === "1";
  } catch {
    return false;
  }
}

export function setSoundOn(on) {
  try {
    window.localStorage.setItem(ON_KEY, on ? "1" : "0");
  } catch {
    // Best-effort — a blocked/full localStorage shouldn't crash the toggle.
  }
}

export function getSoundChoice() {
  try {
    const saved = window.localStorage.getItem(CHOICE_KEY);
    return SOUNDS.some((s) => s.name === saved) ? saved : DEFAULT_SOUND;
  } catch {
    return DEFAULT_SOUND;
  }
}

export function setSoundChoice(name) {
  try {
    window.localStorage.setItem(CHOICE_KEY, name);
  } catch {
    // Best-effort, same as above.
  }
}

// One AudioContext reused across plays — created lazily since some browsers
// only allow it after a user gesture, and there's no reason to pay for one
// before the first sound is ever needed.
let audioCtx = null;
function ctx() {
  if (!audioCtx) {
    const Ctor = window.AudioContext || window.webkitAudioContext;
    if (!Ctor) return null;
    audioCtx = new Ctor();
  }
  if (audioCtx.state === "suspended") audioCtx.resume().catch(() => {});
  return audioCtx;
}

/** Plays a sound by name regardless of the on/off preference — used for
 * previewing a choice in the picker. See playSelectedSound() for the
 * preference-gated version an actual new-message event should call. */
export function playSound(name) {
  const s = SOUNDS.find((x) => x.name === name);
  if (!s) return;
  const ac = ctx();
  if (!ac) return;
  const now = ac.currentTime;
  const master = ac.createGain();
  master.gain.value = 0.18;
  master.connect(ac.destination);

  s.notes.forEach((freq, i) => {
    const osc = ac.createOscillator();
    const gain = ac.createGain();
    osc.type = s.type;
    const start = now + i * 0.09;
    if (s.drop) {
      osc.frequency.setValueAtTime(freq, start);
      osc.frequency.exponentialRampToValueAtTime(Math.max(freq * 0.55, 40), start + s.decay);
    } else {
      osc.frequency.setValueAtTime(freq, start);
    }
    gain.gain.setValueAtTime(0, start);
    gain.gain.linearRampToValueAtTime(1, start + 0.012);
    gain.gain.exponentialRampToValueAtTime(0.001, start + s.decay);
    osc.connect(gain);
    gain.connect(master);
    osc.start(start);
    osc.stop(start + s.decay + 0.05);

    if (s.shimmer) {
      const osc2 = ac.createOscillator();
      const gain2 = ac.createGain();
      osc2.type = "sine";
      osc2.frequency.setValueAtTime(freq * 2.01, start);
      gain2.gain.setValueAtTime(0, start);
      gain2.gain.linearRampToValueAtTime(0.35, start + 0.012);
      gain2.gain.exponentialRampToValueAtTime(0.001, start + s.decay * 0.7);
      osc2.connect(gain2);
      gain2.connect(master);
      osc2.start(start);
      osc2.stop(start + s.decay + 0.05);
    }
  });
}

// Two independent places notice "a new message arrived" — the sidebar's
// unread-count poll, and the message thread itself when it's already open —
// and on rare timing they can both fire for the same arrival. A short
// debounce means at most one sound per burst instead of an audible overlap.
let lastPlayedAt = 0;
const DEBOUNCE_MS = 600;

/** What an actual new-message/new-reply event should call — plays the
 * chosen sound only if the preference is on. */
export function playSelectedSound() {
  if (!isSoundOn()) return;
  const now = Date.now();
  if (now - lastPlayedAt < DEBOUNCE_MS) return;
  lastPlayedAt = now;
  playSound(getSoundChoice());
}
